import { connect } from 'react-redux';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    FormControlLabel,
    RadioGroup,
    Radio,
    Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import _ from 'lodash';

import {
    BUSINESS_CONTACT_TYPE,
} from '../../common/Constants';

import {
    createBusinessContact,
    clearBusinessContact,
    createAnonymousBusinessContact,
    clearAnonymousBusinessContact,
} from '../../actions/ProductListOnDisplay';

import FormDialog from '../../components/Dialog/FormDialog';
import Button from '../../components/Button/CustomButton';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import CleanableInput from '../../components/Input/CleanableInput';
import CustomTypography from '../../components/Typography/CustomTypography';
import Validation from '../../common/Validation';

const useStyles = makeStyles((theme) => ({
    contactDescription: {
        backgroundColor: '#fff2cc',
        borderRadius: '4px',
        color: (props) => props?.contents_text_color,
    },
    radioGroup: {
        width: '100%',
        [theme.breakpoints.up('md')]: {
            justifyContent: 'space-around',
        },
    },
    submitButton: {
        borderRadius: theme.spacing(1),
    },
    formDialogOverflow: {
        overflowY: 'unset',
    },
    spacing: {
        padding: '15px 0',
    },
    colorTextInput: {
        '& input': {
            color: '#4b4b4b',
        },
        '& label.Mui-focused': {
            color: '#4b4b4b',
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: '#4b4b4b',
        },
        '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
                borderColor: '#4b4b4b',
            },
        },
    },
    colorContactInput: {
        '& div': {
            color: '#4b4b4b',
        },
        '& label.Mui-focused': {
            color: '#4b4b4b',
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: '#4b4b4b',
        },
        '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
                borderColor: '#4b4b4b',
            },
        },
    },
    colorIconRadio: {
        '& > span': {
            color: (props) => props?.customColor?.button_background_color?.common_button || props?.button_background_color?.common_button,
        },
        '& .Mui-checked': {
            color: (props) => props?.customColor?.button_background_color?.common_button || props?.button_background_color?.common_button,
        },
    },
}));

const BusinessContactForm = (props) => {
    const {
        isOpen,
        onClose,
        submitContact,
        productId,
        success,
        clearContact,
        showSnackBar,
        isWeakAuth,
        submitAnonymousContact,
        clearAnonymousContact,
        customText,
        customColorExhibitor,
    } = props;
    const [type, setType] = useState(BUSINESS_CONTACT_TYPE.BUY);
    const [message, setMessage] = useState('');
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const intl = useIntl();
    const classes = useStyles(customColorExhibitor);
    const [validateResults, setValidateResults] = useState({});

    useEffect(() => {
        if (isOpen) {
            setMessage('');
            setName('');
            setPhoneNumber('');
            setEmail('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (success === 'Success') {
            onClose();
            showSnackBar();
            clearContact();
            clearAnonymousContact();
        }
    }, [success]);

    const handleSubmitBusinessContact = () => {
        if (validate()) {
            if (isWeakAuth) {
                submitAnonymousContact({
                    type,
                    mail_content: message,
                    product_id: productId,
                    name,
                    phone_number: phoneNumber,
                    email,
                });
            } else {
                submitContact({ type, mail_content: message, product_id: productId });
            }
        }
    };

    const handleSelectType = (event) => {
        setType(_.toNumber(event.target.value));
    };

    const submitButton = () => (
        <>
            <Box style={{ margin: 'auto' }}>

                <Button
                    autoFocus
                    onClick={handleSubmitBusinessContact}
                    size="md"
                    style={{ width: '200px', fontSize: '20px' }}
                    className={classes.submitButton}
                    color="navy"
                    buttonTextColor={customColorExhibitor?.button_text_color}
                    buttonBackgroundColor={customColorExhibitor?.button_background_color?.common_button}
                >
                    {intl.formatMessage({ id: 'organizerBusinessContact.send' })}
                </Button>
            </Box>
        </>
    );

    const renderProductInfoRow = (label, isRequired, content) => (
        <GridContainer className={classes.spacing}>
            <GridItem xs={12} sm={4} md={4}>
                <CustomTypography bold required={isRequired} customColor={customColorExhibitor?.contents_text_color}>
                    <FormattedMessage id={label} />
                </CustomTypography>
            </GridItem>
            <GridItem xs={12} sm={8} md={8}>
                {content}
            </GridItem>
        </GridContainer>
    );

    const validate = () => {
        const defaultBusinessContact = {
            message: [
                () => Validation.isNotEmpty(message),
            ],
        };

        const validateBusinessContact = !isWeakAuth
            ? defaultBusinessContact
            : {
                ...defaultBusinessContact,
                name: [
                    () => Validation.isNotEmpty(name),
                    () => Validation.validateMaxLength(name, 255, typeof name),
                ],
                email: [
                    () => Validation.isNotEmpty(email),
                    () => Validation.validateMaxLength(email, 255, typeof email),
                    () => Validation.validateEmail(email),
                ],
                phoneNumber: [
                    () => Validation.validatePhoneNumber(phoneNumber.trim()),
                ],
            };

        const validateResult = Validation.validateAllRules(validateBusinessContact);

        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            return false;
        }
        setValidateResults({});
        return true;
    };

    return (
        <FormDialog
            isOpen={isOpen}
            title={customText?.button_name?.business_contact || intl.formatMessage({ id: 'productRegister.businessContactNewText' })}
            handleClose={onClose}
            actions={submitButton()}
            maxWidth="sm"
            titleAlign="center"
            visibleCustomizeCloseBtn
            customColor={customColorExhibitor?.contents_text_color}
            paperProps={{
                style: {
                    overflowY: 'unset',
                },
            }}
        >
            <Box display="flex" flexDirection="column" alignItems="center" width="90%" margin="auto">
                <RadioGroup
                    row
                    onChange={handleSelectType}
                    value={type}
                    defaultValue={BUSINESS_CONTACT_TYPE.BUY}
                    className={classes.radioGroup}
                >
                    <FormControlLabel
                        control={<Radio color="primary" />}
                        label={<CustomTypography customColor="#4b4b4b">{intl.formatMessage({ id: 'organizerBusinessContact.inquiries' })}</CustomTypography>}
                        value={BUSINESS_CONTACT_TYPE.BUY}
                        className={classes.colorIconRadio}
                    />
                    <FormControlLabel
                        control={<Radio color="primary" />}
                        label={<CustomTypography customColor="#4b4b4b">{intl.formatMessage({ id: 'organizerBusinessContact.buyOrNegotiate' })}</CustomTypography>}
                        value={BUSINESS_CONTACT_TYPE.NEGOTIATE}
                        className={classes.colorIconRadio}
                    />
                </RadioGroup>

                {isWeakAuth && (
                    <>
                        {renderProductInfoRow('name', true, (
                            <>
                                <CleanableInput
                                    classes={{ root: classes.colorTextInput }}
                                    defaultValue={name}
                                    onChange={(valueInput) => setName(valueInput)}
                                    error={!!validateResults.name}
                                    helperText={validateResults.name
                                        ? intl.formatMessage(
                                            { id: validateResults.name.message },
                                            { maxLength: 255 },
                                        ) : ''}
                                />
                                <CustomTypography customColor={customColorExhibitor?.contents_text_color}>
                                    {intl.formatMessage({ id: 'organizerBusinessContact.enterCompanyOrCompanyName' })}
                                </CustomTypography>
                            </>
                        ))}
                        {renderProductInfoRow('phoneNumber', false, (
                            <CleanableInput
                                classes={{ root: classes.colorTextInput }}
                                defaultValue={phoneNumber}
                                onChange={(valueInput) => setPhoneNumber(valueInput)}
                                error={!!validateResults.phoneNumber}
                                helperText={validateResults.phoneNumber
                                    ? intl.formatMessage(
                                        { id: validateResults.phoneNumber.message },
                                        { maxLength: 255 },
                                    ) : ''}
                            />
                        ))}
                        {renderProductInfoRow('mailAddress', true, (
                            <CleanableInput
                                classes={{ root: classes.colorTextInput }}
                                defaultValue={email}
                                onChange={(valueInput) => setEmail(valueInput)}
                                error={!!validateResults.email}
                                helperText={validateResults.email
                                    ? intl.formatMessage(
                                        { id: validateResults.email.message },
                                        { maxLength: 255 },
                                    ) : ''}
                            />
                        ))}
                    </>
                )}

                <Box padding={1} className={classes.contactDescription} marginTop={5} marginBottom={2}>
                    <ul>
                        <li>
                            <b>{intl.formatMessage({ id: 'organizerBusinessContact.descriptionOne' })}</b>
                            <br />
                            <span>{intl.formatMessage({ id: 'organizerBusinessContact.descriptionTwo' })}</span>
                        </li>
                        <li>
                            <b>{intl.formatMessage({ id: 'organizerBusinessContact.descriptionThree' })}</b>
                            <br />
                            <span>{intl.formatMessage({ id: 'organizerBusinessContact.descriptionFour' })}</span>
                        </li>
                        <li>
                            <b>{intl.formatMessage({ id: 'organizerBusinessContact.descriptionFive' })}</b>
                            <br />
                            <span>{intl.formatMessage({ id: 'organizerBusinessContact.descriptionSix' })}</span>
                        </li>
                    </ul>
                </Box>
                <TextField
                    classes={{ root: classes.colorContactInput }}
                    rows={10}
                    variant="outlined"
                    type="text"
                    multiline
                    fullWidth
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    error={validateResults.message}
                    helperText={validateResults.message
                        ? intl.formatMessage(
                            { id: validateResults.message.message },
                        ) : ''}
                />
            </Box>
        </FormDialog>
    );
};

BusinessContactForm.defaultProps = {
    isOpen: false,
    onClose: () => {},
    submitContact: () => {},
    productId: 0,
    success: '',
    clearContact: () => {},
    showSnackBar: () => {},
    submitAnonymousContact: () => {},
    clearAnonymousContact: () => {},
    customText: {},
    customColorExhibitor: {},
};

BusinessContactForm.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    submitContact: PropTypes.func,
    productId: PropTypes.number,
    success: PropTypes.string,
    clearContact: PropTypes.func,
    showSnackBar: PropTypes.func,
    isWeakAuth: PropTypes.bool.isRequired,
    submitAnonymousContact: PropTypes.func,
    clearAnonymousContact: PropTypes.func,
    customText: PropTypes.oneOfType([() => null, PropTypes.object]),
    customColorExhibitor: PropTypes.oneOfType([() => null, PropTypes.object]),
};

const mapStateToProps = (state) => ({
    productId: state.productManagement.getProduct.data && state.productManagement.getProduct.data.id,
    success: state.productListOnDisplay.createContact.success || state.productListOnDisplay.createAnonymousContact.success,
    isWeakAuth: !!state.auth.credential.data?.isWeakAuth,
});

const mapDispatchToProps = (dispatch) => ({
    submitContact: (params) => {
        dispatch(createBusinessContact(params));
    },
    submitAnonymousContact: (params) => {
        dispatch(createAnonymousBusinessContact(params));
    },
    clearContact: () => {
        dispatch(clearBusinessContact());
    },
    clearAnonymousContact: () => {
        dispatch(clearAnonymousBusinessContact());
    },
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(BusinessContactForm);
